import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import { Input } from "@/components/design-system/input";
import SelectWithCheckbox from "@/components/design-system/SelectWithCheckbox";
import { getSchoolDropdownService } from "@/service/base.service";
import {
    createGroupSchoolAdmin,
    fetchGroupSchoolAdminById,
    selectGroupSchoolAdminActionLoading,
    updateGroupSchoolAdmin,
} from "@/store/slices/groupSchoolAdminSlice";
import { GroupSchoolAdminModel } from "@/types/group-school-admin.model";
import { successToast } from "@/utils/constants";
import { AppDispatch } from "@store";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";

interface GroupSchoolAdminProps {
    readonly viewMode?: boolean;
    readonly editMode?: boolean;
}

interface GroupSchoolAdminFormData {
    name: string;
    title: string;
    email: string;
    contactNumber: string;
    schools: any[];
}

// Field configuration for reusability and scalability
interface FieldConfig {
    id: keyof GroupSchoolAdminFormData;
    label: string;
    placeholder: string;
    type?: string;
    required?: boolean;
    maxLength?: number;
    validationProps?: any; // Additional props for specific field types (e.g., number validation)
}

const validationProps = {
    className: "no-spinner",
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace") {
            e.preventDefault();
        }
    },
    onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
        e.currentTarget.blur();
    },
}

const groupSchoolAdminDetailsFields: FieldConfig[] = [
    {
        id: "name",
        label: "Name",
        placeholder: "Enter Name",
        required: true,
        maxLength: 60,
    },
    {
        id: "title",
        label: "Title",
        placeholder: "Enter Title",
        required: true,
        maxLength: 60,
    },
    {
        id: "email",
        label: "Email Address",
        placeholder: "Enter Email Address",
        type: "email",
        required: true,
    },
    {
        id: "contactNumber",
        label: "Contact Number",
        placeholder: "Enter Contact Number",
        type: "number",
        required: true,
        validationProps
    },
];

export default function GroupSchoolAdminDetails({
    viewMode = false,
    editMode = false,
}: GroupSchoolAdminProps) {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const actionLoading = useSelector(selectGroupSchoolAdminActionLoading);
    const { id } = useParams();
    const [groupSchoolAdminData, setGroupSchoolAdminData] = useState<GroupSchoolAdminModel | null>(null);
    const [isLoading, setIsLoading] = useState(viewMode || editMode);
    const [schools, setSchools] = useState<{ id: string; name: string }[]>([]);

    const initialFetchDone = useRef(false);

    useEffect(() => {
        // Only fetch if we haven't done the initial fetch yet
        if (!initialFetchDone.current) {
            const fetchGrades = async () => {
                try {
                    const { data } = await getSchoolDropdownService({
                        search: "",
                    });
                    setSchools(data.data);
                } catch (error) {
                    console.error("Error fetching clients:", error);
                }
            };

            fetchGrades();

            // Mark that we've done the initial fetch
            initialFetchDone.current = true;
        }
    }, [dispatch, viewMode]);

    // Fetch school data when in view or edit mode
    useEffect(() => {
        if ((viewMode || editMode) && id) {
            setIsLoading(true);
            dispatch(fetchGroupSchoolAdminById(id))
                .unwrap()
                .then((data) => {
                    setGroupSchoolAdminData(data);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching admin:", error);
                    // errorToast("Error", "Failed to load admin details");
                    setIsLoading(false);
                    navigate("/group-school-admin");
                });
        }
    }, [viewMode, editMode, id, dispatch, navigate]);

    // Initialize react-hook-form
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isValid },
    } = useForm<GroupSchoolAdminFormData>({
        defaultValues: {
            name: "",
            title: "",
            email: "",
            contactNumber: "",
            schools: [],
        },
        mode: "onBlur",
        reValidateMode: "onChange",
        criteriaMode: "firstError",
    });

    // Set form values when admin data is loaded in view or edit mode
    useEffect(() => {
        if ((viewMode || editMode) && groupSchoolAdminData) {
            setValue("name", groupSchoolAdminData?.name);
            setValue("title", groupSchoolAdminData?.title);
            setValue("email", groupSchoolAdminData?.email);
            setValue("contactNumber", groupSchoolAdminData?.contact_number);

            // Handle school data which could be array of strings or objects
            const schools = Array.isArray(groupSchoolAdminData.schools)
                ? groupSchoolAdminData.schools.map((school) =>
                    typeof school === "string" ? school : school?.id || ""
                )
                : [];
            setValue("schools", schools);
        }
    }, [groupSchoolAdminData, setValue, viewMode, editMode]);

    // Form submission handler
    const onFormSubmit = async (data: GroupSchoolAdminFormData) => {
        try {
            // Transform form data to match the SchoolModel interface
            const groupSchoolAdminData: Partial<GroupSchoolAdminModel> = {
                name: data.name,
                title: data.title,
                email: data.email,
                contact_number: data.contactNumber,
                // is_active: true,
                school_ids: data?.schools
            };

            if (editMode && id) {
                // Update existing admin
                const resultAction = await dispatch(updateGroupSchoolAdmin({ id, groupSchoolAdminData }));

                if (updateGroupSchoolAdmin.fulfilled.match(resultAction)) {
                    // Show success toast
                    successToast(`Admin Updated Successfully.`, "");

                    navigate(-1);
                }
            } else {
                // Create new admin
                const resultAction = await dispatch(
                    createGroupSchoolAdmin(groupSchoolAdminData as GroupSchoolAdminModel)
                );

                if (createGroupSchoolAdmin.fulfilled.match(resultAction)) {
                    // Show success toast
                    successToast(`Admin Added Successfully.`, "");

                    navigate("/group-school-admin");
                }
            }
        } catch (error) {
            console.error("Error saving Admin:", error);
        }
    };

    // Form validation rules
    const getValidationRules = (field: FieldConfig) => {
        const rules: Record<string, unknown> = {};

        if (field.required) {
            switch (field.id) {
                case "name":
                    rules.required = "Please enter the name.";
                    break;
                case "title":
                    rules.required = "Please enter the title.";
                    break;
                case "email":
                    rules.required = "Please enter a valid email address.";
                    break;
                case "contactNumber":
                    rules.required = "Please enter a valid mobile number.";
                    break;
                default:
                    rules.required = `${field.label} is required`;
            }
        }

        if (field.id === "email") {
            // Custom validation for email to handle spaces and double @ symbols
            rules.validate = {
                validFormat: (value: string) => {
                    // More comprehensive email validation
                    // - Validates local part and domain format
                    // - Ensures domain has at least one dot
                    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                    if (!emailRegex.test(value)) {
                        return "Please enter a valid email address";
                    }

                    // Additional check for double @ symbols
                    if ((value.match(/@/g) || []).length !== 1) {
                        return "Email address should contain exactly one @ symbol";
                    }

                    return true;
                },
            };
        }

        if (field.id === "contactNumber") {
            rules.pattern = {
                value: /^[0-9]{10}$/,
                message: "Please enter a valid mobile number",
            };
        }

        return rules;
    };

    const handleCancel = () => {
        navigate("/group-school-admin");
    };

    // Reusable field renderer for consistent UI
    const renderField = (field: FieldConfig) => (
        <div key={field.id} className="w-full">
            <Input
                id={field.id}
                {...register(field.id, getValidationRules(field))}
                label={field.label}
                type={field.type ?? "text"}
                placeholder={field.placeholder}
                required={field.required}
                maxLength={field.maxLength || undefined}
                errorMessage={errors[field.id]?.message as string}
                disabled={viewMode || (editMode && field.id === "email")}
                readOnly={viewMode || (editMode && field.id === "email")}
                {...field.validationProps}
            />
        </div>
    );

    return (
        <div className="h-full flex flex-col gap-6">
            {isLoading ? (
                <div className="bg-white rounded-[12px] h-[calc(100vh-120px)] flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
                        <p className="mt-4 text-gray-600">Loading school details...</p>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-[12px] h-full">
                    <form
                        onSubmit={handleSubmit(onFormSubmit)}
                        className="w-full h-full flex flex-col"
                    >
                        {/* Group School Admin Details Section */}
                        <div className={`px-[32px] py-[33px] flex flex-col flex-1`}>
                            <div className="mb-8">
                                <div className="">
                                    <Breadcrumbs
                                        title={
                                            viewMode
                                                ? "Admin Details"
                                                : editMode
                                                    ? "Edit Admin"
                                                    : "Add Admin"
                                        }
                                        onClick={() => navigate("/group-school-admin")}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {groupSchoolAdminDetailsFields.map(renderField)}
                                    {/* School Selection */}
                                    <div className="w-full ">
                                        <SelectWithCheckbox
                                            id="school"
                                            label="School"
                                            placeholder="Select a schools"
                                            options={schools.map((school) => ({
                                                value: school.id,
                                                label: school.name,
                                            }))}
                                            selectedText="Schools"
                                            showAbove={false}
                                            maxHeight="220px"
                                            clampToViewportBelow={true}
                                            value={watch("schools")}
                                            onChange={(value) =>
                                                setValue("schools", value as string[], {
                                                    shouldValidate: true,
                                                })
                                            }
                                            required={true}
                                            disabled={viewMode}
                                            error={errors.schools?.message as string}
                                        />

                                        <input
                                            type="hidden"
                                            {...register("schools", {
                                                required: !viewMode ? "School is required" : false,
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>

                        </div>
                        {/* Form Actions - Only show in add or edit mode */}
                        <div className="flex h-[98px] px-[32px] justify-end space-x-4 border-t border-gray-200 pt-6 shadow-[0px_4px_24px_8px_#0000001F]">
                            {viewMode ? (
                                <Button
                                    type="button"
                                    className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                                    onClick={handleCancel}
                                >
                                    Close
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        type="button"
                                        className="h-[48px] w-[161px]"
                                        variant="primary-outline"
                                        onClick={handleCancel}
                                        disabled={actionLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                                        variant="primary"
                                        disabled={!isValid || actionLoading}
                                        loading={actionLoading}
                                    >
                                        {editMode ? "Update" : "Add"}
                                    </Button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
